import { useState } from 'react';
import type { PrivateView, PublicView, WolfPickAction } from '@ringmaster/shared';
import { useRoom } from '../net/useRoom.js';
import { FixedActionBar } from '../ui/FixedActionBar.js';
import { Glyph } from '../ui/Glyph.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';
import clawGouge from '../assets/ceremony/claw-gouge.webp';

/** Short haptic on a target selection (guarded — many devices lack it). */
function tapHaptic() {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(30);
    } catch {
      /* haptics unsupported */
    }
  }
}

/**
 * NightWolfScreen — wolf's night action.
 * Crimson glow, "Choose your victim" + candidates list + fixed confirm bar.
 * Shows packmates' live picks + packAgreement indicator.
 * After confirm: "The deed is done. Sleep."
 */
export function NightWolfScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const { send } = useRoom();
  const action = privateView.action as WolfPickAction;
  const [selected, setSelected] = useState<string | null>(action.myPick ?? null);
  const [confirmed, setConfirmed] = useState(false);

  const candidates = action.candidates;

  // Map player id to name from publicView.
  const nameMap = Object.fromEntries(publicView.players.map((p) => [p.id, p.name]));

  function handleConfirm() {
    if (!selected) return;
    send({ type: 'SUBMIT_NIGHT_TARGET', targetId: selected });
    setConfirmed(true);
  }

  if (confirmed) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #000000 0%, #320001 100%)',
          gap: 'var(--space-5)',
          padding: 'var(--space-5)',
        }}
      >
        <Glyph name="wolfHead" size={48} tone="blood" />
        <p
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 22,
            textAlign: 'center',
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          The deed is done.
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, textAlign: 'center', margin: 0 }}>
          Sleep now.
        </p>
        {/* Dim moon */}
        <div
          style={{
            marginTop: 'var(--space-4)',
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #802636 0%, #320001 100%)',
            boxShadow: '0 0 30px 8px rgba(153,0,0,0.25)',
            animation: 'moon-breathe 4s ease-in-out infinite',
          }}
        />
        <style>{`
          @keyframes moon-breathe {
            0%, 100% { opacity: 0.5; transform: scale(1); }
            50%       { opacity: 0.75; transform: scale(1.05); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #0a0000 0%, #320001 100%)',
        padding: 'var(--space-6) var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-6))',
        // Room for the fixed confirm bar (+ host RingmasterBar).
        paddingBottom: 110 + actionOffset,
        gap: 'var(--space-5)',
        overflow: 'hidden',
      }}
    >
      <WolfStyles />

      {/* Breathing crimson vignette (±2%, 4s) — the pack's held breath. */}
      <div aria-hidden className="wolf-vignette" />
      {/* Blood motes hanging in the dark (phone-tier capped). */}
      <ParticleLayer preset="bloodMotes" intensity={0.6} slot={0} />

      {/* Header */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-2)' }}>
          <Glyph name="wolfHead" size={44} tone="blood" />
        </div>
        <h1 className="wolf-title">Choose your prey</h1>
        <p style={{ color: 'var(--blood-6, #ff5a4f)', fontSize: 14, margin: 'var(--space-3) 0 0', opacity: 0.85 }}>
          Tap to choose, then confirm
        </p>
      </div>

      {/* Packmates + agreement indicator */}
      {privateView.packmates && privateView.packmates.length > 0 && (
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            background: 'rgba(128,38,54,0.2)',
            border: '1px solid rgba(153,0,0,0.35)',
            borderRadius: 16,
            padding: 'var(--space-3) var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'rgba(255,150,150,0.8)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Pack picks
            </span>
            {privateView.packAgreement === true && (
              <span
                style={{
                  background: 'rgba(153,0,0,0.5)',
                  border: '1px solid rgba(153,0,0,0.7)',
                  borderRadius: 'var(--radius-pill)',
                  padding: '3px 10px',
                  fontSize: 11,
                  color: '#ffaaaa',
                  fontWeight: 600,
                }}
              >
                {privateView.packmates.length + 1}/{privateView.packmates.length + 1} agree
              </span>
            )}
          </div>
          {privateView.packmates.map((pm) => (
            <div
              key={pm.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 14,
                color: 'var(--text-secondary)',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Glyph name="wolfHead" size={14} tone="blood" />
                {pm.name}
              </span>
              <span style={{ color: pm.currentPickId ? 'var(--blood-6, #ff5a4f)' : 'var(--text-tertiary)' }}>
                {pm.currentPickId ? `→ ${nameMap[pm.currentPickId] ?? pm.currentPickId}` : 'thinking…'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Candidate plaques — selection draws a claw-mark border. */}
      <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {candidates.map((id) => {
            const playerName = nameMap[id] ?? id;
            const isSelected = selected === id;
            return (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => {
                    if (selected !== id) tapHaptic();
                    setSelected(id);
                  }}
                  className={`wolf-plaque${isSelected ? ' is-selected' : ''}`}
                  aria-pressed={isSelected}
                >
                  {/* Claw-gouge accent revealed on selection. */}
                  {isSelected && <img src={clawGouge} alt="" aria-hidden className="wolf-plaque-claw" />}
                  <span
                    className="wolf-radio"
                    aria-hidden
                    style={{
                      borderColor: isSelected ? 'var(--blood-5, #c22f3e)' : 'var(--surface-stroke)',
                      background: isSelected ? 'var(--blood-4, #990000)' : 'transparent',
                    }}
                  />
                  <span style={{ position: 'relative', zIndex: 1 }}>{playerName}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Confirm button — fixed at the bottom, always visible */}
      <FixedActionBar offsetBottom={actionOffset}>
        <button
          type="button"
          className="rm-hover"
          disabled={!selected}
          onClick={handleConfirm}
          style={{
            width: '100%',
            fontFamily: 'var(--font-ui)',
            fontWeight: 600,
            fontSize: 16,
            borderRadius: 'var(--radius-pill)',
            padding: '16px 24px',
            background: selected ? 'linear-gradient(135deg, var(--blood-3, #802636) 0%, var(--blood-4, #990000) 100%)' : 'rgba(255,255,255,0.1)',
            color: selected ? '#ffffff' : 'var(--text-tertiary)',
            border: 'none',
            cursor: selected ? 'pointer' : 'not-allowed',
            minHeight: 52,
            boxShadow: selected ? 'var(--rim-blood, 0 0 24px rgba(153,0,0,0.55))' : 'none',
            transition: 'all var(--duration-base) var(--ease-standard)',
          }}
        >
          {selected ? `Confirm: ${nameMap[selected] ?? selected}` : 'Select a target'}
        </button>
      </FixedActionBar>
    </div>
  );
}

function WolfStyles() {
  return (
    <style>{`
      .wolf-title {
        font-family: var(--font-display); font-size: 28px; margin: 0;
        color: var(--text-primary); display: inline-block; position: relative;
        padding-bottom: 8px;
      }
      /* Blood underline flourish. */
      .wolf-title::after {
        content: ''; position: absolute; left: 12%; right: 12%; bottom: 0; height: 2px;
        background: linear-gradient(90deg, transparent, var(--blood-5, #c22f3e) 50%, transparent);
      }
      .wolf-vignette {
        position: absolute; inset: 0; pointer-events: none; z-index: 0;
        background: radial-gradient(120% 100% at 50% 50%, transparent 45%, rgba(50,0,1,0.55) 100%);
        animation: wolf-breathe 4s ease-in-out infinite;
      }
      @keyframes wolf-breathe { 0%,100% { opacity: 0.9; } 50% { opacity: 1; } }
      .wolf-plaque {
        position: relative; width: 100%; min-height: 56px; text-align: left;
        padding: 15px var(--space-4); border-radius: var(--radius-md); overflow: hidden;
        border: 1px solid var(--surface-stroke);
        background: linear-gradient(180deg, var(--ink-3, #241619) 0%, var(--ink-2, #150d0f) 100%);
        color: var(--text-primary); cursor: pointer; font-family: var(--font-ui); font-size: 16px;
        display: flex; align-items: center; gap: var(--space-3);
        box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
        transition: border-color var(--duration-fast) var(--ease-standard), transform var(--duration-fast) var(--ease-standard), background var(--duration-fast) var(--ease-standard);
      }
      .wolf-plaque.is-selected {
        border-color: var(--blood-5, #c22f3e);
        box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), var(--rim-blood, 0 0 24px rgba(153,0,0,0.55));
      }
      .wolf-plaque:active { transform: scale(0.99); }
      @media (hover: hover) and (pointer: fine) {
        .wolf-plaque:hover:not(.is-selected) { border-color: rgba(194,47,62,0.55); background: linear-gradient(180deg, #2e1d20 0%, #1c1214 100%); transform: translateY(-1px); }
      }
      .wolf-plaque-claw {
        position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
        opacity: 0.55; mix-blend-mode: screen; pointer-events: none;
        animation: wolf-claw-in 0.3s var(--ease-dramatic);
      }
      @keyframes wolf-claw-in { from { opacity: 0; transform: scale(1.15); } to { opacity: 0.55; transform: none; } }
      .wolf-radio {
        width: 20px; height: 20px; border-radius: 50%; border: 2px solid var(--surface-stroke);
        display: inline-block; flex-shrink: 0; position: relative; z-index: 1;
        transition: all var(--duration-fast) var(--ease-standard);
      }
      @media (prefers-reduced-motion: reduce) {
        .wolf-vignette, .wolf-plaque-claw { animation: none; }
      }
    `}</style>
  );
}
